import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import LocationSearch from '@/components/LocationSearch';
import { 
  Sparkles, CheckCircle, ArrowRight, Phone, Star,
  Home, Users, Briefcase, Shield, Wifi, Car, Coffee,
  Utensils, Tv, Wind, Waves, Bed
} from 'lucide-react';

const requirements = [
  { icon: Bed, title: 'Fully Furnished', description: 'Complete furniture including beds, wardrobes, dining' },
  { icon: Utensils, title: 'Equipped Kitchen', description: 'Modular kitchen with all appliances' },
  { icon: Tv, title: 'Entertainment', description: 'Smart TV with streaming services' },
  { icon: Wifi, title: 'High-Speed WiFi', description: 'Reliable internet connectivity' },
  { icon: Wind, title: 'Climate Control', description: 'AC in all rooms' },
  { icon: Car, title: 'Parking', description: 'Dedicated parking space' },
];

const propertyTypes = [
  { value: 'guest_house', label: 'Guest House', description: 'Ideal for short stays and corporate guests' },
  { value: 'co_working', label: 'Shared Co-Working Space', description: 'Work-friendly spaces with amenities' },
  { value: 'vacation_rental', label: 'Vacation Rental', description: 'Holiday homes and weekend getaways' },
];

const benefits = [
  'Professional property evaluation',
  'Demographics and occupancy analysis',
  'Revenue potential assessment',
  'Daily/weekly housekeeping services',
  'Maintenance and upkeep management',
  'Premium listing on ZenStudios platform',
  'Professional photography',
  'Higher ratings and reviews',
];

const ZenSpace = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    propertyType: 'guest_house',
    propertyAddress: '',
    bedrooms: '',
    amenities: '',
    message: ''
  });

  // Validation helpers
  const isValidEmail = (email) => {
    return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone';
    }
    
    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.propertyAddress.trim()) {
      newErrors.propertyAddress = 'Property address is required';
    }
    
    if (!formData.bedrooms) {
      newErrors.bedrooms = 'Please select number of bedrooms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/zen-space', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        city: formData.city || null,
        property_type: formData.propertyType,
        property_address: formData.propertyAddress,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        amenities: formData.amenities || null,
        message: formData.message || null
      });
      
      toast.success(`Application submitted! Your Request ID: ${response.data.request_id}`);
      
      // Open WhatsApp with prefilled message
      const whatsappMsg = `Hi ZenStudios! I want to list my property as a Zen Space. My Request ID: ${response.data.request_id}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="zen-space-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" /> Zen Spaces
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              List Your Property as a Zen Space
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Transform your fully furnished property into a premium Zen Space. 
              We handle everything from housekeeping to guest management while you earn 
              hassle-free rental income.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button 
                size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 px-8"
                onClick={() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Apply Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20want%20to%20know%20more%20about%20Zen%20Spaces.', '_blank')}
              >
                <Phone className="w-5 h-5 mr-2" />
                Talk to Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Zen Space Requirements</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Your property must meet these standards to qualify for Zen Spaces branding. 
              Properties should be hotel-suite quality with all mandatory amenities.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requirements.map((req, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <req.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{req.title}</h3>
                  <p className="text-sm text-gray-600">{req.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Types of Zen Spaces</h2>
            <p className="text-gray-600">We accept these property types for Zen Spaces</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {propertyTypes.map((type, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {type.value === 'guest_house' && <Home className="w-8 h-8 text-primary" />}
                  {type.value === 'co_working' && <Briefcase className="w-8 h-8 text-primary" />}
                  {type.value === 'vacation_rental' && <Star className="w-8 h-8 text-primary" />}
                </div>
                <h3 className="text-xl font-semibold mb-2">{type.label}</h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">What ZenStudios Provides</h2>
              <p className="text-gray-600 mb-8">
                Once your property is approved, we take care of everything to ensure 
                the highest standards and maximum revenue for you.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary/5 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold mb-4">Important Note</h3>
              <p className="text-gray-600 mb-4">
                Some aspects are outside ZenStudios&apos; control. During our evaluation, 
                we may identify issues that the owner needs to address on highest 
                priority to avoid inconvenience to renters.
              </p>
              <p className="text-gray-600">
                These may include structural repairs, plumbing issues, or compliance 
                requirements. We&apos;ll provide a detailed report with recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-gray-50" id="apply-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Apply to List as Zen Space</h2>
            <p className="text-gray-600">
              Submit your property details and our team will evaluate and get back to you
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({...formData, name: e.target.value});
                      if (errors.name) setErrors({...errors, name: ''});
                    }}
                    placeholder="Your name"
                    className={`rounded-xl mt-1 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({...formData, phone: e.target.value});
                      if (errors.phone) setErrors({...errors, phone: ''});
                    }}
                    placeholder="+91 98765 43210"
                    className={`rounded-xl mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({...formData, email: e.target.value});
                      if (errors.email) setErrors({...errors, email: ''});
                    }}
                    placeholder="your@email.com"
                    className={`rounded-xl mt-1 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label>City *</Label>
                  <LocationSearch
                    value={formData.city}
                    onChange={(val) => {
                      setFormData({...formData, city: val});
                      if (errors.city) setErrors({...errors, city: ''});
                    }}
                    placeholder="Search city..."
                    types="city,town"
                    required
                    className={`rounded-xl mt-1 ${errors.city ? '[&_input]:border-red-500' : ''}`}
                    data-testid="zenspace-city"
                  />
                  {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Property Type *</Label>
                  <Select value={formData.propertyType} onValueChange={(v) => setFormData({...formData, propertyType: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Number of Bedrooms *</Label>
                  <Input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => {
                      setFormData({...formData, bedrooms: e.target.value});
                      if (errors.bedrooms) setErrors({...errors, bedrooms: ''});
                    }}
                    placeholder="e.g., 3"
                    className={`rounded-xl mt-1 ${errors.bedrooms ? 'border-red-500' : ''}`}
                  />
                  {errors.bedrooms && <p className="text-red-500 text-xs mt-1">{errors.bedrooms}</p>}
                </div>
              </div>

              <div>
                <Label>Property Address *</Label>
                <Input
                  value={formData.propertyAddress}
                  onChange={(e) => {
                    setFormData({...formData, propertyAddress: e.target.value});
                    if (errors.propertyAddress) setErrors({...errors, propertyAddress: ''});
                  }}
                  placeholder="Full property address"
                  className={`rounded-xl mt-1 ${errors.propertyAddress ? 'border-red-500' : ''}`}
                />
                {errors.propertyAddress && <p className="text-red-500 text-xs mt-1">{errors.propertyAddress}</p>}
              </div>

              <div>
                <Label>Available Amenities</Label>
                <Textarea
                  value={formData.amenities}
                  onChange={(e) => setFormData({...formData, amenities: e.target.value})}
                  placeholder="List amenities available (AC, TV, Kitchen appliances, etc.)"
                  rows={3}
                  className="rounded-xl mt-1"
                />
              </div>

              <div>
                <Label>Additional Information</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Any other details about your property..."
                  rows={3}
                  className="rounded-xl mt-1"
                />
              </div>

              <Button type="submit" className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                Our team will evaluate your property and contact you within 48-72 hours.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZenSpace;
